import { useEffect, useState } from 'react';
import Card from '../../components/card/Card';
import P from '../../components/texts/p/P';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import Image from '../../components/image/Image';
import { getSyndicatedOperations } from '../../services/GuaranteesService';
import { formatCurrency } from '../../utils/Masks';

interface SindicatedTabProps {
  guaranteeId: string;
  guaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement';
}

interface SyndicatedOperation {
  syndicated_total_amount: number;
  percentage: number;
  monetization_amount: number;
}

const SindicatedTab: React.FC<SindicatedTabProps> = ({ guaranteeId, guaranteeType }) => {
  const [data, setData] = useState<SyndicatedOperation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSyndicatedData();
  }, [guaranteeId, guaranteeType]);

  const fetchSyndicatedData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSyndicatedOperations(guaranteeType, guaranteeId);
      
      if (response.success && 'data' in response) {
        setData(response.data);
      } else {
        setError('No se encontraron datos de operaciones sindicadas');
      }
    } catch (err: any) {
      console.error('Error fetching syndicated operations:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Error al cargar las operaciones sindicadas';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: 'Monto total de la garantía',
      selector: (row: SyndicatedOperation) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.syndicated_total_amount ? formatCurrency(row.syndicated_total_amount) : '-'}
          </P>
        </Card>
      ),
      grow: 2,
    },
    {
      name: 'Porcentaje',
      selector: (row: SyndicatedOperation) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.percentage !== undefined && row.percentage !== null 
              ? `${row.percentage.toFixed(2)}%` 
              : '-'}
          </P>
        </Card>
      ),
      grow: 2,
    },
    {
      name: 'Importe avalado SGR',
      selector: (row: SyndicatedOperation) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.monetization_amount ? formatCurrency(row.monetization_amount) : '-'}
          </P>
        </Card>
      ),
      grow: 2,
    },
  ];

  if (loading) {
    return (
      <Card margin={{ top: '4' }}>
        <TableSkeleton rows={1} columns={columns.length} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        display='grid'
        justifyContent='center'
        margin={{ top: '11', bottom: '11' }}
        width='100%'
      >
        <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
          <Image name='folder_empty' />
        </Card>
        <P variant='primary' align='center' withMargin={false} weight='bold'>
          {error}
        </P>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card
        display='grid'
        justifyContent='center'
        margin={{ top: '11', bottom: '11' }}
        width='100%'
      >
        <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
          <Image name='folder_empty' />
        </Card>
        <P variant='primary' align='center' withMargin={false} weight='bold'>
          No se encontraron operaciones sindicadas.
        </P>
      </Card>
    );
  }

  return (
    <Card margin={{ top: '4' }}>
      <Card>
        <P
          type='2'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Operaciones Sindicadas
        </P>
        <Table
          data={[data]}
          columns={columns}
          tableData={{ size: 1, totalElements: 1 }}
          variant='weak'
        />
      </Card>
    </Card>
  );
};

export default SindicatedTab;

